import { useEffect, useMemo, useRef, useState } from 'react'
import { useUIStore } from '@/store/app-store'

export type ThemeMode = 'light' | 'dark' | 'system'

function getSystemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyThemeToDocument(isDark: boolean) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (isDark) {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
  // Hint the browser for native form controls
  root.style.colorScheme = isDark ? 'dark' : 'light'
}

export function useTheme() {
  const theme = useUIStore((s) => s.theme)
  const setTheme = useUIStore((s) => s.setTheme)

  const [systemDark, setSystemDark] = useState(getSystemPrefersDark())
  const mediaQueryRef = useRef<MediaQueryList | null>(null)

  // Subscribe to system theme changes when in system mode
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    mediaQueryRef.current = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    // Attach only when using system
    if (theme === 'system') {
        try {
          mediaQueryRef.current.addEventListener('change', handler)
        } catch {
          // Safari <14 fallback: older MediaQueryList API
          mediaQueryRef.current.addListener(handler)
        }
      }
    return () => {
      if (!mediaQueryRef.current) return
        try {
          mediaQueryRef.current.removeEventListener('change', handler)
        } catch {
          // Safari <14 fallback: older MediaQueryList API
          mediaQueryRef.current.removeListener(handler)
        }
    }
  }, [theme])

  const resolved = useMemo<'light' | 'dark'>(() => {
    if (theme === 'system') return systemDark ? 'dark' : 'light'
    return theme
  }, [theme, systemDark])

  // Apply to document whenever resolved changes
  useEffect(() => {
    applyThemeToDocument(resolved === 'dark')
  }, [resolved])

  const toggle = (next?: ThemeMode) => {
    if (next) return setTheme(next)
    // default toggle cycles light -> dark -> system -> light
    const order: ThemeMode[] = ['light', 'dark', 'system']
    const idx = order.indexOf(theme)
    const nextMode = order[(idx + 1) % order.length]
    setTheme(nextMode)
  }

  return { theme, resolved, setTheme, toggle }
}
